import { faCheck, faExclamation, faExclamationTriangle, faFlag, faGlobe } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Checkbox, Table, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import { TimesheetRow } from './timesheetsApi'
import {
  calculateSelectedHours,
  costCodeLabel,
  DEFAULT_SORT_FIELD,
  divisionLabel,
  paginationSummary,
  formatEnded,
  formatExported,
  formatHours,
  formatStarted,
  formatTimecardDate,
  nextGridState,
  rowIndicator,
  RowIndicators,
  selectableRowIds,
  TimesheetsFilters,
  TimesheetSortField,
  TimesheetSortFields,
  toggleRowSelection,
  visitIdForRow,
} from './timesheetsModel'
import { useTimesheetsQuery } from './timesheetsQueries'
import './TimesheetsGrid.css'

const warningColor = '#faad14'
const gpsIconColor = '#428bca'

export type TimesheetsSelection = {
  ids: string[]
  hours: number
}

type Props = {
  deletedView: boolean
  filters: TimesheetsFilters
  onSelectionChange?: (selection: TimesheetsSelection) => void
  onEditVisit?: (visitId: string) => void
  onEditUnbillable?: (periodId: string) => void
  onCustomerClick?: (row: TimesheetRow) => void
}

const TimesheetsGrid = ({ deletedView, filters, onSelectionChange, onEditVisit, onEditUnbillable, onCustomerClick }: Props) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [sortField, setSortField] = useState<TimesheetSortField>(DEFAULT_SORT_FIELD)
  const [sortDescending, setSortDescending] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const timesheetsQuery = useTimesheetsQuery({ deletedView, page, pageSize, sortField, sortDescending, filters })
  const rows = timesheetsQuery.data?.data ?? []
  const total = timesheetsQuery.data?.recordsFiltered ?? 0

  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange

  const filtersKey = JSON.stringify(filters)
  useEffect(() => {
    setPage(1)
  }, [filtersKey, deletedView])

  useEffect(() => {
    setSelectedIds([])
    onSelectionChangeRef.current?.({ ids: [], hours: 0 })
  }, [timesheetsQuery.data])

  const updateSelection = (ids: string[]) => {
    setSelectedIds(ids)
    onSelectionChange?.({ ids, hours: calculateSelectedHours(rows, ids) })
  }

  const handleRowToggle = (row: TimesheetRow, shiftKey: boolean) => {
    updateSelection(toggleRowSelection(selectedIds, selectableRowIds(rows), row.id, shiftKey))
  }

  const handleSelectAll = () => {
    const selectable = selectableRowIds(rows)
    updateSelection(selectedIds.length === selectable.length ? [] : selectable)
  }

  const selectable = selectableRowIds(rows)
  const allSelected = selectable.length > 0 && selectedIds.length === selectable.length

  const toggleCell = (row: TimesheetRow) => ({
    onClick: (event: MouseEvent<HTMLElement>) => handleRowToggle(row, event.shiftKey),
  })

  const editCell = (row: TimesheetRow) => ({
    onClick: () => {
      if (row.isUnbillableTime) {
        onEditUnbillable?.(row.id)
        return
      }
      const visitId = visitIdForRow(row)
      if (visitId) {
        onEditVisit?.(visitId)
      }
    },
    style: { cursor: 'pointer' },
  })

  const customerCell = (row: TimesheetRow) => ({
    onClick: () => {
      if (row.customerId) {
        onCustomerClick?.(row)
      }
    },
    style: row.customerId ? { cursor: 'pointer' } : undefined,
  })

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _tableFilters: unknown,
    sorter: SorterResult<TimesheetRow> | SorterResult<TimesheetRow>[]
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter
    const next = nextGridState(
      { page, pageSize, sortField, sortDescending },
      pagination,
      { columnKey: single?.columnKey as string | number | undefined, order: single?.order }
    )
    setSortField(next.sortField)
    setSortDescending(next.sortDescending)
    setPageSize(next.pageSize)
    setPage(next.page)
  }

  const columns: ColumnsType<TimesheetRow> = [
    {
      title: <Checkbox checked={allSelected} onChange={handleSelectAll} />,
      key: 'select',
      width: 40,
      onCell: toggleCell,
      render: (_, row) => {
        const indicator = rowIndicator(row)
        if (indicator === RowIndicators.Exported) {
          return <FontAwesomeIcon icon={faCheck} />
        }
        if (indicator === RowIndicators.Unsubmitted) {
          return <Tooltip title="Timesheet is not submitted yet."><FontAwesomeIcon icon={faExclamation} style={{ color: warningColor }} /></Tooltip>
        }
        if (indicator === RowIndicators.MissingQboLink) {
          return <Tooltip title="Click the customer name to link this customer to the matching QuickBooks Online customer before exporting">
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: warningColor }} />
          </Tooltip>
        }
        return <Checkbox checked={selectedIds.includes(row.id)} style={{ pointerEvents: 'none' }} />
      },
    },
    { title: 'Date', key: TimesheetSortFields.TimecardDate, sorter: true, defaultSortOrder: 'ascend', onCell: toggleCell, render: (_, row) => formatTimecardDate(row.timecardDate) },
    { title: 'Crew Member', key: TimesheetSortFields.CrewMember, sorter: true, onCell: toggleCell, render: (_, row) => row.crewMember },
    { title: 'Cost Code', key: TimesheetSortFields.CostCode, sorter: true, ellipsis: true, onCell: toggleCell, render: (_, row) => costCodeLabel(row) },
    { title: 'Division', key: TimesheetSortFields.Division, sorter: true, ellipsis: true, onCell: toggleCell, render: (_, row) => divisionLabel(row) },
    { title: 'No.', key: TimesheetSortFields.ProjectNo, sorter: true, onCell: toggleCell, render: (_, row) => row.projectNo },
    { title: 'Project', key: TimesheetSortFields.ProjectName, sorter: true, ellipsis: true, onCell: toggleCell, render: (_, row) => row.projectName },
    { title: 'Workarea', key: TimesheetSortFields.ProjectWorkarea, sorter: true, onCell: toggleCell, render: (_, row) => row.projectWorkarea },
    {
      title: 'Customer',
      key: TimesheetSortFields.Customer,
      sorter: true,
      ellipsis: true,
      onCell: customerCell,
      render: (_, row) => <>
        {row.customerQboId == null && !row.isUnbillableTime && <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: warningColor, marginRight: 4 }} />}
        {row.customer}
        {row.customerQboDisplayName && <small style={{ marginLeft: 4 }}>{row.customerQboDisplayName}</small>}
      </>,
    },
    { title: 'Created By', key: TimesheetSortFields.CreatedBy, sorter: true, onCell: toggleCell, render: (_, row) => row.createdBy },
    { title: 'Started', key: TimesheetSortFields.Started, sorter: true, align: 'right', onCell: editCell, render: (_, row) => formatStarted(row.started) },
    { title: 'Ended', key: TimesheetSortFields.Ended, sorter: true, align: 'right', onCell: editCell, render: (_, row) => formatEnded(row.ended) },
    {
      title: '',
      key: 'gpsFlag',
      width: 40,
      onCell: editCell,
      render: (_, row) => <>
        {row.gpsFlag?.hasGpsData && <FontAwesomeIcon icon={faGlobe} style={{ color: gpsIconColor }} />}
        {row.gpsFlag?.isFlagged && <FontAwesomeIcon icon={faFlag} style={{ color: gpsIconColor, marginLeft: 4 }} />}
      </>,
    },
    { title: 'Hours', key: TimesheetSortFields.TotalTime, sorter: true, align: 'right', onCell: editCell, render: (_, row) => formatHours(row.totalTime) },
    { title: 'Exported', key: TimesheetSortFields.Exported, sorter: true, align: 'right', onCell: toggleCell, render: (_, row) => formatExported(row.exported) },
  ]

  return <Table
    className="timesheets-grid"
    rowKey="id"
    columns={columns}
    dataSource={rows}
    loading={timesheetsQuery.isLoading || timesheetsQuery.isFetching}
    size="small"
    onChange={handleTableChange}
    pagination={{
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: ['50', '100', '500'],
      showTotal: (totalEntries, range) =>
        paginationSummary(range[0], range[1], totalEntries, timesheetsQuery.data?.recordsTotal ?? totalEntries),
    }}
  />
}

export default TimesheetsGrid
